import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Chip, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Divider,
  Alert, Card, CardContent, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import { Download, Close, ZoomIn, NavigateBefore, NavigateNext } from "@mui/icons-material";
import ArrowBackRoundedIcon   from "@mui/icons-material/ArrowBackRounded";
import ShieldRoundedIcon      from "@mui/icons-material/ShieldRounded";
import { useParams, useNavigate } from "react-router-dom";
import { getData, postData } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:"#007C84", tealDk:"#016268", tealLt:"#E0F4F5",
  emerald:"#059669", emeraldLt:"#D1FAE5",
  rose:"#E11D48", roseLt:"#FFE4E6",
  amber:"#D97706", amberLt:"#FEF3C7",
  indigo:"#4F46E5", indigoLt:"#EEF2FF",
  orange:"#FF7900", orangeLt:"#FFF0E0",
  bg:"#F7FAFC", border:"#E2E8F0", text:"#0F172A", muted:"#64748B",
};

const STATUS_SUT = {
  filed:            { bg:T.indigoLt,  color:T.indigo  },
  under_review:     { bg:T.amberLt,   color:T.amber   },
  evidence_pending: { bg:T.roseLt,    color:T.rose    },
  approved:         { bg:T.emeraldLt, color:T.emerald },
  rejected:         { bg:T.roseLt,    color:T.rose    },
  closed:           { bg:"#F1F5F9",   color:T.muted   },
};

const SCard = ({ children, accent }) => (
  <Card elevation={0} sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden", mb:3 }}>
    {accent && <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />}
    {children}
  </Card>
);

const SHead = ({ title }) => (
  <Typography sx={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif", mb:2 }}>{title}</Typography>
);
const statusColors = {
  filed: "#1565C0", under_review: "#E65100", evidence_pending: "#BF360C",
  approved: "#2E7D32", rejected: "#C62828", closed: "#616161",
};

export default function ProtectionClaimDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveAmount, setApproveAmount] = useState("");
  const [approveNote, setApproveNote] = useState("");

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Image viewer
  const [viewerOpen, setViewerOpen] = useState(false);

  // Ratings
  const [ratings, setRatings] = useState([]);
  const [adminOutcome, setAdminOutcome] = useState(0);
  const [adminProcess, setAdminProcess] = useState(0);
  const [adminComment, setAdminComment] = useState("");
  const [adminRatingSubmitted, setAdminRatingSubmitted] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const fetchRatings = useCallback(async () => {
    try {
      const url = API_ENDPOINTS.getClaimRatings.replace(":claimId", id);
      const res = await getData(url);
      const result = res?.result || res?.data?.result || [];
      setRatings(Array.isArray(result) ? result : []);
      // Check if admin already rated
      const adminR = (Array.isArray(result) ? result : []).find(r => r.user_type === "admin");
      if (adminR) {
        setAdminRatingSubmitted(true);
        setAdminOutcome(adminR.outcome_rating);
        setAdminProcess(adminR.process_rating);
        setAdminComment(adminR.comment || "");
      }
    } catch (err) { console.error("Failed to fetch ratings:", err); }
  }, [id]);

  const fetchClaim = useCallback(async () => {
    setLoading(true);
    try {
      const url = API_ENDPOINTS.getProtectionClaimById.replace(":claim_id", id);
      const res = await getData(url);
      const result = res?.result || res?.data?.result;
      if (result) setClaim(result);
    } catch (err) {
      console.error("Failed to fetch claim:", err);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchClaim();
    fetchRatings();
  }, [fetchClaim, fetchRatings]);

  const handleAdminRating = async () => {
    if (adminOutcome < 1 || adminProcess < 1) return;
    try {
      const url = API_ENDPOINTS.submitClaimRating.replace(":claimId", id);
      const adminData = JSON.parse(localStorage.getItem("userData") || "{}");
      await postData(url, {
        outcome_rating: adminOutcome,
        process_rating: adminProcess,
        comment: adminComment || undefined,
        user_type: "admin",
        admin_id: adminData.admin_id || "ADM2026-0001",
      });
      setAdminRatingSubmitted(true);
      fetchRatings();
    } catch (err) { console.error("Failed to submit admin rating:", err); }
  };

  const handleTriage = async () => {
    setActionLoading(true);
    try {
      const url = API_ENDPOINTS.triageProtectionClaim.replace(":claim_id", id);
      await postData(url, {});
      fetchClaim();
    } catch (err) { console.error(err); }
    setActionLoading(false);
  };

  // Evidence request dialog state
  const [evidenceRequestOpen, setEvidenceRequestOpen] = useState(false);
  const [evidenceTarget, setEvidenceTarget] = useState("owner_driver");
  const [evidenceMessage, setEvidenceMessage] = useState("");

  const handleRequestEvidence = async () => {
    setActionLoading(true);
    try {
      const url = API_ENDPOINTS.requestClaimEvidence.replace(":claim_id", id);
      await postData(url, { target: evidenceTarget, message: evidenceMessage || undefined });
      setEvidenceRequestOpen(false);
      setEvidenceMessage("");
      fetchClaim();
    } catch (err) { console.error(err); }
    setActionLoading(false);
  };

  const handleApprove = async () => {
    if (!approveAmount || parseFloat(approveAmount) <= 0) return;
    setActionLoading(true);
    try {
      const url = API_ENDPOINTS.approveProtectionClaim.replace(":claim_id", id);
      await postData(url, { amount: parseFloat(approveAmount), note: approveNote });
      setApproveOpen(false);
      fetchClaim();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to approve claim";
      window.alert(msg);
      console.error(err);
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const url = API_ENDPOINTS.rejectProtectionClaim.replace(":claim_id", id);
      await postData(url, { reason: rejectReason });
      setRejectOpen(false);
      fetchClaim();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to reject claim";
      window.alert(msg);
      console.error(err);
    }
    setActionLoading(false);
  };

  const handleMarkFraudulent = async () => {
    if (!window.confirm("Mark this claim as fraudulent? This will block future protection for this customer.")) return;
    setActionLoading(true);
    try {
      const url = API_ENDPOINTS.markClaimFraudulent.replace(":claim_id", id);
      await postData(url, {});
      fetchClaim();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to mark as fraudulent";
      window.alert(msg);
      console.error(err);
    }
    setActionLoading(false);
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>;
  if (!claim) return <Typography sx={{ p: 3 }}>Claim not found</Typography>;

  const canTriage = claim.status === "filed";
  const canRequestEvidence = ["under_review", "evidence_pending"].includes(claim.status);
  const canDecide = ["under_review", "evidence_pending"].includes(claim.status);

    const sCfg = STATUS_SUT[claim.status] || { bg:"#F1F5F9", color:T.muted };

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* Hero header */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
          <IconButton onClick={() => navigate("/app/protection-claims")}
            sx={{ bgcolor:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:"10px", color:"#fff", "&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
            <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
          </IconButton>
          <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>Cargo Protection Claims</Typography>
        </Box>
        <Box sx={{ display:"flex", alignItems:"center", gap:2, flexWrap:"wrap" }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <ShieldRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Box sx={{ display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap", mb:0.4 }}>
              <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{claim.claim_ref}</Typography>
              <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:sCfg.bg, border:`1px solid ${sCfg.color}22` }}>
                <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:sCfg.color }} />
                <Typography sx={{ fontSize:11, fontWeight:700, color:sCfg.color }}>{claim.status?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</Typography>
              </Box>
              {claim.is_fraudulent && (
                <Box sx={{ display:"inline-flex", px:1.2, py:0.3, bgcolor:T.roseLt, borderRadius:"8px" }}>
                  <Typography sx={{ fontSize:11, fontWeight:700, color:T.rose }}>FRAUDULENT</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize:12, color:"rgba(255,255,255,0.7)" }}>
              {claim.Booking?.plan_tier?.toUpperCase()} Plan · {claim.category?.replace(/_/g," ")} · Filed {claim.filed_at ? new Date(claim.filed_at).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {claim.is_fraudulent && (
        <Alert severity="error" sx={{ mb:2, borderRadius:"12px" }}>This claim has been marked as FRAUDULENT</Alert>
      )}

      <Grid container spacing={2}>
        {/* Claim Info */}
        <Grid item xs={12} md={6}>
          <SCard accent={T.teal}><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <SHead title="Claim Information" />
              <InfoRow label="Category" value={claim.category?.replace(/_/g, " ")} />
              <InfoRow label="Filed At" value={claim.filed_at ? new Date(claim.filed_at).toLocaleString() : "-"} />
              <InfoRow label="Description" value={claim.description} />
              {claim.approved_amount > 0 && <InfoRow label="Approved Amount" value={`\u20B9${claim.approved_amount}`} />}
              {claim.rejection_reason && <InfoRow label="Rejection Reason" value={claim.rejection_reason} />}
              {claim.appeal_filed && <InfoRow label="Appeal" value={claim.appeal_reason || "Filed"} />}
            </CardContent></SCard>
        </Grid>

        {/* Protection Info */}
        <Grid item xs={12} md={6}>
          <SCard accent={T.indigo}><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <SHead title="Protection Details" />
              <InfoRow label="Plan Tier"      value={claim.Booking?.plan_tier?.toUpperCase()} />
              <InfoRow label="Declared Value" value={`\u20B9${claim.Booking?.declared_value?.toLocaleString()||0}`} />
              <InfoRow label="Coverage Limit" value={`\u20B9${claim.Booking?.coverage_limit?.toLocaleString()||0}`} />
              <InfoRow label="Premium Paid"   value={`\u20B9${claim.Booking?.premium_amount||0}`} />
              <InfoRow label="Certificate"    value={claim.Booking?.certificate_ref} />
            </CardContent></SCard>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={6}>
          <SCard accent={T.teal}><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <SHead title="Customer" />
              <InfoRow label="Name"   value={`${claim.Customer?.first_name||""} ${claim.Customer?.last_name||""}`.trim()} />
              <InfoRow label="Mobile" value={claim.Customer?.mobile_num} />
              <InfoRow label="Email"  value={claim.Customer?.email} />
            </CardContent></SCard>
        </Grid>

        {/* Customer Evidence */}
        <Grid item xs={12} md={6}>
          <SCard accent={T.indigo}><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2 }}>
                <Box sx={{ width:8, height:8, borderRadius:"50%", bgcolor:T.indigo }} />
                <Typography sx={{ fontSize:14, fontWeight:700, color:T.indigo, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  Customer Evidence ({(claim.Evidence||[]).filter(e=>e.submitted_by_user_type==="customer").length})
                </Typography>
              </Box>
              {claim.description && (
                <Typography variant="body2" sx={{ mb: 1.5, p: 1.5, bgcolor: "#F5F5F5", borderRadius: 1, fontStyle: "italic" }}>
                  "{claim.description}"
                </Typography>
              )}
              {(claim.Evidence || []).filter(e => e.submitted_by_user_type === "customer" && e.description && e.description !== claim.description).map((ev, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 1, p: 1, bgcolor: "#E3F2FD", borderRadius: 1, fontSize: 12 }}>
                  <b>Additional:</b> "{ev.description}"
                  {ev.submitted_at && <Typography component="span" variant="caption" color="text.disabled"> — {new Date(ev.submitted_at).toLocaleString()}</Typography>}
                </Typography>
              ))}
              <EvidenceGrid evidence={(claim.Evidence || []).filter(e => e.submitted_by_user_type === "customer")}
                onView={(idx) => { setViewerIndex(idx); setViewerOpen(true); }} startIndex={0} />
            </CardContent></SCard>
        </Grid>

        {/* Owner/Driver Evidence */}
        <Grid item xs={12} md={6}>
          <SCard accent={T.orange}><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2 }}>
                <Box sx={{ width:8, height:8, borderRadius:"50%", bgcolor:T.orange }} />
                <Typography sx={{ fontSize:14, fontWeight:700, color:T.orange, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  Owner/Driver Evidence ({(claim.Evidence||[]).filter(e=>e.submitted_by_user_type!=="customer").length})
                </Typography>
              </Box>
              {(() => {
                const ownerEvidence = (claim.Evidence || []).filter(e => e.submitted_by_user_type !== "customer");
                const ownerDesc = ownerEvidence.find(e => e.description)?.description;
                return ownerDesc ? (
                  <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: "#FFF3E0", borderRadius: 1, fontStyle: "italic" }}>
                    "{ownerDesc}"
                  </Typography>
                ) : null;
              })()}
              <EvidenceGrid
                evidence={(claim.Evidence || []).filter(e => e.submitted_by_user_type !== "customer")}
                onView={(idx) => {
                  const custCount = (claim.Evidence || []).filter(e => e.submitted_by_user_type === "customer").length;
                  setViewerIndex(custCount + idx);
                  setViewerOpen(true);
                }}
                startIndex={0}
                emptyText="No evidence from owner/driver yet"
              />
            </CardContent></SCard>
        </Grid>
      </Grid>

      {/* Audit Log */}
      <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
        <SHead title="Audit Timeline" />
        <Box sx={{ position: "relative", pl: 3 }}>
          {(() => {
            const custName = `${claim.Customer?.first_name || ""} ${claim.Customer?.last_name || ""}`.trim() || "Customer";
            const adminName = claim.AssignedAdmin ? `${claim.AssignedAdmin.first_name || ""} ${claim.AssignedAdmin.last_name || ""}`.trim() : "Admin";
            const ownerName = claim.Trip?.Truck?.Owner ? `${claim.Trip.Truck.Owner.first_name || ""} ${claim.Trip.Truck.Owner.last_name || ""}`.trim() : "Owner/Driver";

            return [
              { time: claim.createdAt, label: "Claim Filed", detail: `by ${custName} — Category: ${claim.category?.replace(/_/g, " ")}`, color: "#1565C0" },
              claim.assigned_to_admin_id && { time: null, label: "Triaged by Admin", detail: `Assigned to ${adminName}`, color: "#7C3AED" },
              claim.evidence_requested_at && { time: claim.evidence_requested_at, label: `Info Requested from ${claim.evidence_request_target === "both" ? `${custName} + ${ownerName}` : claim.evidence_request_target === "customer" ? custName : ownerName}`, detail: claim.evidence_request_message ? `"${claim.evidence_request_message}" — Deadline: ${new Date(claim.evidence_deadline).toLocaleString()}` : `Deadline: ${new Date(claim.evidence_deadline).toLocaleString()}`, color: "#E65100" },
              (() => {
                const reqAt = claim.evidence_requested_at ? new Date(claim.evidence_requested_at) : null;
                const custPost = (claim.Evidence || []).filter(e =>
                  e.submitted_by_user_type === "customer" && reqAt && new Date(e.submitted_at) > reqAt);
                if (custPost.length > 0) return { time: custPost[0]?.submitted_at, label: `${custName} Responded`, detail: `${custPost.length} file(s) submitted`, color: "#1565C0" };
                return null;
              })(),
              (() => {
                const reqAt = claim.evidence_requested_at ? new Date(claim.evidence_requested_at) : null;
                const ownerPost = (claim.Evidence || []).filter(e =>
                  e.submitted_by_user_type !== "customer" && reqAt && new Date(e.submitted_at) > reqAt);
                if (ownerPost.length > 0) return { time: ownerPost[0]?.submitted_at, label: `${ownerName} Responded`, detail: `${ownerPost.length} file(s) submitted`, color: "#2E7D32" };
                const allOwner = (claim.Evidence || []).filter(e => e.submitted_by_user_type !== "customer");
                if (!reqAt && allOwner.length > 0) return { time: null, label: `${ownerName} Evidence`, detail: `${allOwner.length} file(s)`, color: "#2E7D32" };
                return null;
              })(),
              claim.status === "approved" && { time: claim.updatedAt, label: "Claim Approved", detail: `by ${adminName} — Payout: \u20B9${claim.approved_amount}${claim.review_note ? ` | ${claim.review_note}` : ""}`, color: "#2E7D32" },
              claim.status === "rejected" && { time: claim.updatedAt, label: "Claim Rejected", detail: `by ${adminName} — ${claim.rejection_reason}`, color: "#C62828" },
              claim.appeal_filed && { time: null, label: `${custName} Appealed`, detail: claim.appeal_reason, color: "#E65100" },
              claim.is_fraudulent && { time: null, label: "Marked Fraudulent", detail: `by ${adminName}`, color: "#C62828" },
              claim.closed_at && { time: claim.closed_at, label: "Claim Closed", detail: null, color: "#616161" },
            ].filter(Boolean).map((entry, i) => (
              <Box key={i} sx={{ display: "flex", mb: 2, position: "relative" }}>
                <Box sx={{
                  position: "absolute", left: -19, top: 4, width: 12, height: 12,
                  borderRadius: "50%", bgcolor: entry.color, border: "2px solid #fff",
                  boxShadow: `0 0 0 2px ${entry.color}33`,
                }} />
                {i < 8 && <Box sx={{ position: "absolute", left: -14, top: 16, width: 2, height: "calc(100% + 8px)", bgcolor: "#e0e0e0" }} />}
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" fontWeight={600} color={entry.color}>{entry.label}</Typography>
                  {entry.detail && <Typography variant="caption" color="text.secondary">{entry.detail}</Typography>}
                  {entry.time && <Typography variant="caption" display="block" color="text.disabled">{new Date(entry.time).toLocaleString()}</Typography>}
                </Box>
              </Box>
            ))
          })()}
        </Box>
      </CardContent></SCard>

      {/* Feedback & Ratings */}
      {claim.status === "closed" && (
        <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
          <SHead title="Feedback & Ratings" />
          {ratings.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
              {ratings.filter(r => r.user_type !== "admin").map((r, i) => (
                <Card key={i} elevation={0} sx={{ border: "1px solid #e0e0e0", flex: "1 1 280px", minWidth: 280 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Chip label={r.is_anonymous ? `Anonymous (${r.user_type})` : `${r.user_name || r.user_type}`}
                        size="small" sx={{ fontWeight: 600 }} />
                      <Typography variant="caption" color="text.secondary">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Outcome</Typography>
                        <Box>{[1, 2, 3, 4, 5].map(s => <span key={s} style={{ color: s <= r.outcome_rating ? "#FFA000" : "#E0E0E0", fontSize: 18 }}>{"\u2605"}</span>)}</Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Process</Typography>
                        <Box>{[1, 2, 3, 4, 5].map(s => <span key={s} style={{ color: s <= r.process_rating ? "#FFA000" : "#E0E0E0", fontSize: 18 }}>{"\u2605"}</span>)}</Box>
                      </Box>
                    </Box>
                    {r.comment && <Typography variant="body2" sx={{ fontStyle: "italic", color: "#555" }}>"{r.comment}"</Typography>}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No feedback received yet</Typography>
          )}

          {/* Admin self-rating */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Admin Rating (internal only)</Typography>
          {adminRatingSubmitted ? (
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box><Typography variant="caption">Resolution Quality</Typography>
                <Box>{[1, 2, 3, 4, 5].map(s => <span key={s} style={{ color: s <= adminOutcome ? "#FFA000" : "#E0E0E0", fontSize: 18 }}>{"\u2605"}</span>)}</Box></Box>
              <Box><Typography variant="caption">Process Adherence</Typography>
                <Box>{[1, 2, 3, 4, 5].map(s => <span key={s} style={{ color: s <= adminProcess ? "#FFA000" : "#E0E0E0", fontSize: 18 }}>{"\u2605"}</span>)}</Box></Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                <Box>
                  <Typography variant="caption">Resolution Quality</Typography>
                  <Box>{[1, 2, 3, 4, 5].map(s => (
                    <span key={s} onClick={() => setAdminOutcome(s)}
                      style={{ cursor: "pointer", color: s <= adminOutcome ? "#FFA000" : "#E0E0E0", fontSize: 24 }}>{"\u2605"}</span>
                  ))}</Box>
                </Box>
                <Box>
                  <Typography variant="caption">Process Adherence</Typography>
                  <Box>{[1, 2, 3, 4, 5].map(s => (
                    <span key={s} onClick={() => setAdminProcess(s)}
                      style={{ cursor: "pointer", color: s <= adminProcess ? "#FFA000" : "#E0E0E0", fontSize: 24 }}>{"\u2605"}</span>
                  ))}</Box>
                </Box>
              </Box>
              <TextField size="small" fullWidth label="Internal note (optional)" value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)} sx={{ mb: 2 }} />
              <Button variant="contained" size="small" onClick={handleAdminRating}
                disabled={adminOutcome < 1 || adminProcess < 1}>Submit Admin Rating</Button>
            </Box>
          )}
        </CardContent></SCard>
      )}

      {/* Actions */}
      <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
        <SHead title="Admin Actions" />
        <Box sx={{ display:"flex", gap:1.5, flexWrap:"wrap" }}>
          {canTriage && (
            <Button variant="contained" disableElevation onClick={handleTriage} disabled={actionLoading}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:T.teal, "&:hover":{ bgcolor:T.tealDk } }}>
              Triage (Assign to Me)
            </Button>
          )}
          {canRequestEvidence && (
            <Button variant="outlined" onClick={() => setEvidenceRequestOpen(true)} disabled={actionLoading}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, borderColor:T.amber, color:T.amber, "&:hover":{ bgcolor:T.amberLt } }}>
              Request Additional Info
            </Button>
          )}
          {canDecide && (
            <>
              <Button variant="contained" color="success" onClick={() => { setApproveAmount(claim.Booking?.coverage_limit?.toString() || ""); setApproveOpen(true); }} disabled={actionLoading}>
                Approve Payout
              </Button>
              <Button variant="contained" color="error" onClick={() => setRejectOpen(true)} disabled={actionLoading}>
                Reject Claim
              </Button>
            </>
          )}
          {!claim.is_fraudulent && claim.status !== "closed" && (
            <Button variant="outlined" color="error" onClick={handleMarkFraudulent} disabled={actionLoading}>
              Mark Fraudulent
            </Button>
          )}
        </Box>
      </CardContent></SCard>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Claim Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Coverage Limit: {"\u20B9"}{claim.Booking?.coverage_limit?.toLocaleString() || 0}
          </Typography>
          <TextField fullWidth label="Payout Amount (₹)" type="number" value={approveAmount}
            onChange={(e) => setApproveAmount(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Note (optional)" multiline rows={3} value={approveNote}
            onChange={(e) => setApproveNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
            Approve & Pay
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Claim</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Rejection Reason (required)" multiline rows={3} value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Request Dialog */}
      <Dialog open={evidenceRequestOpen} onClose={() => setEvidenceRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Additional Information</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select who should provide additional information. They will receive a notification with a 48-hour deadline.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Request From</InputLabel>
            <Select value={evidenceTarget} label="Request From" onChange={(e) => setEvidenceTarget(e.target.value)}>
              <MenuItem value="owner_driver">Owner / Driver</MenuItem>
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="both">Both (Customer + Owner/Driver)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Custom Message (optional)" multiline rows={3} value={evidenceMessage}
            onChange={(e) => setEvidenceMessage(e.target.value)}
            placeholder="e.g., Please provide photos of the package at the delivery point showing the damage..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceRequestOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRequestEvidence} disabled={actionLoading}>
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: "#000", minHeight: "80vh" } }}>
        {(() => {
          const evidence = claim?.Evidence || [];
          const current = evidence[viewerIndex];
          if (!current) return null;
          return (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1 }}>
                <Typography sx={{ color: "#fff", fontSize: 14 }}>
                  Evidence {viewerIndex + 1} of {evidence.length}
                  {current.submitted_by_user_type && ` — by ${current.submitted_by_user_type}`}
                </Typography>
                <Box>
                  <Tooltip title="Download">
                    <IconButton onClick={() => {
                      const link = document.createElement("a");
                      link.href = current.file_url;
                      link.download = `evidence_${claim.claim_ref}_${viewerIndex + 1}`;
                      link.target = "_blank";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }} sx={{ color: "#fff" }}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton onClick={() => setViewerOpen(false)} sx={{ color: "#fff" }}>
                      <Close />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, position: "relative", minHeight: "60vh" }}>
                {evidence.length > 1 && (
                  <IconButton onClick={() => setViewerIndex((viewerIndex - 1 + evidence.length) % evidence.length)}
                    sx={{ position: "absolute", left: 8, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                    <NavigateBefore fontSize="large" />
                  </IconButton>
                )}
                <img src={current.file_url} alt={`Evidence ${viewerIndex + 1}`}
                  style={{ maxWidth: "90%", maxHeight: "70vh", objectFit: "contain", borderRadius: 4 }}
                  onError={(e) => { e.target.alt = "Failed to load image"; }} />
                {evidence.length > 1 && (
                  <IconButton onClick={() => setViewerIndex((viewerIndex + 1) % evidence.length)}
                    sx={{ position: "absolute", right: 8, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                    <NavigateNext fontSize="large" />
                  </IconButton>
                )}
              </Box>
              {current.description && (
                <Typography sx={{ color: "#aaa", textAlign: "center", pb: 2, fontSize: 13 }}>
                  {current.description}
                </Typography>
              )}
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}

function InfoRow({ label, value }) {
  const T2 = { muted:"#64748B", text:"#0F172A", border:"#E2E8F0" };
  return (
    <Box sx={{ display:"flex", alignItems:"flex-start", py:1, borderBottom:`1px solid ${T2.border}`, "&:last-of-type":{ borderBottom:"none" } }}>
      <Typography sx={{ fontSize:11, fontWeight:700, color:T2.muted, textTransform:"uppercase", letterSpacing:"0.3px", minWidth:140, flexShrink:0 }}>{label}</Typography>
      <Typography sx={{ fontSize:13, fontWeight:600, color:T2.text, flex:1, textAlign:"right" }}>{value || "—"}</Typography>
    </Box>
  );
}

function EvidenceGrid({ evidence, onView, startIndex = 0, emptyText = "No evidence" }) {
  if (!evidence || evidence.length === 0) {
    return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
      {evidence.map((ev, idx) => (
        <Tooltip key={ev.id || idx} title={ev.description || "Click to view"} arrow>
          <Box
            onClick={() => onView(idx)}
            sx={{
              width: 100, height: 100, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden",
              cursor: "pointer", position: "relative",
              "&:hover": { border: "2px solid #1565C0", "& .zoom-icon": { opacity: 1 } },
            }}
          >
            <img src={ev.file_url} alt="evidence" style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.target.src = ""; e.target.alt = "Failed"; }} />
            <Box className="zoom-icon" sx={{
              position: "absolute", bottom: 4, right: 4, opacity: 0, transition: "opacity 0.2s",
              bgcolor: "rgba(0,0,0,0.5)", borderRadius: "50%", p: 0.3,
            }}>
              <ZoomIn sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}